import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const TIERS = ["STANDARD", "SILVER", "GOLD", "PLATINUM"];
const DISCOUNT = { STANDARD: 0, SILVER: 5, GOLD: 10, PLATINUM: 15 };
const EMPTY_DEALER = { name: "", code: "", phone: "", email: "", region: "", pricing_tier: "STANDARD", credit_limit: 0 };

export default function Pricing() {
  const [dealers, setDealers] = useState([]);
  const [edits, setEdits] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_DEALER);
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/sales/dealers"); setDealers(data); };
  useEffect(() => { load(); }, []);

  const save = async (d) => {
    const e = edits[d.id] || {};
    try {
      await api.post("/distributor/dealer-pricing", {
        dealer_id: d.id,
        pricing_tier: e.pricing_tier || d.pricing_tier || "STANDARD",
        credit_limit: e.credit_limit ?? d.credit_limit ?? 0,
      });
      toast.success(`${d.name} updated`); load();
      setEdits({ ...edits, [d.id]: undefined });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const submitDealer = async () => {
    if (!form.name.trim()) { toast.error("Dealer name is required"); return; }
    setSaving(true);
    try {
      const { data: dealer } = await api.post("/sales/dealers", {
        name: form.name.trim(),
        code: form.code.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        region: form.region.trim() || null,
      });
      // Immediately apply tier + credit limit if given
      if (form.pricing_tier !== "STANDARD" || Number(form.credit_limit) > 0) {
        await api.post("/distributor/dealer-pricing", {
          dealer_id: dealer.id,
          pricing_tier: form.pricing_tier,
          credit_limit: Number(form.credit_limit) || 0,
        });
      }
      toast.success(`${dealer.name} added`);
      setAddOpen(false);
      setForm(EMPTY_DEALER);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add dealer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="distributor-pricing">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Dealer pricing & credit</h1>
          <p className="text-muted-foreground">Assign pricing tiers (STANDARD 0% / SILVER 5% / GOLD 10% / PLATINUM 15%) and credit limits per dealer.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_DEALER); setAddOpen(true); }} data-testid="pricing-add-dealer-btn" className="shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Add dealer
        </Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Dealer</TableHead><TableHead>Region</TableHead><TableHead>Tier</TableHead><TableHead>Discount</TableHead><TableHead>Credit limit (₹)</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {dealers.map((d) => {
              const e = edits[d.id] || {};
              const tier = e.pricing_tier || d.pricing_tier || "STANDARD";
              const climit = e.credit_limit ?? d.credit_limit ?? 0;
              return (
                <TableRow key={d.id} data-testid={`pricing-row-${d.id}`}>
                  <TableCell><div className="font-semibold">{d.name}</div><div className="text-[10px] font-mono text-muted-foreground">{d.code}</div></TableCell>
                  <TableCell>{d.region || "—"}</TableCell>
                  <TableCell>
                    <Select value={tier} onValueChange={(v) => setEdits({ ...edits, [d.id]: { ...e, pricing_tier: v } })}>
                      <SelectTrigger className="w-32" data-testid={`pricing-tier-${d.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Badge variant="outline">{DISCOUNT[tier]}% off</Badge></TableCell>
                  <TableCell>
                    <Input type="number" value={climit} onChange={(ev) => setEdits({ ...edits, [d.id]: { ...e, credit_limit: parseFloat(ev.target.value || "0") } })} className="w-32" data-testid={`pricing-limit-${d.id}`} />
                  </TableCell>
                  <TableCell className="text-right"><Button size="sm" onClick={() => save(d)} disabled={!edits[d.id]} data-testid={`pricing-save-${d.id}`}>Save</Button></TableCell>
                </TableRow>
              );
            })}
            {dealers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No dealers registered.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-dealer-dialog">
          <DialogHeader><DialogTitle>Add dealer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="dealer-name">Dealer name *</Label>
              <Input id="dealer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ashok Distributors" data-testid="add-dealer-name" />
            </div>
            <div>
              <Label htmlFor="dealer-code">Code</Label>
              <Input id="dealer-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="DLR-001" data-testid="add-dealer-code" />
            </div>
            <div>
              <Label htmlFor="dealer-region">Region</Label>
              <Input id="dealer-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Gujarat" data-testid="add-dealer-region" />
            </div>
            <div>
              <Label htmlFor="dealer-phone">Phone</Label>
              <Input id="dealer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91-98xxxxxxxx" data-testid="add-dealer-phone" />
            </div>
            <div>
              <Label htmlFor="dealer-email">Email</Label>
              <Input id="dealer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="dealer@example.com" data-testid="add-dealer-email" />
            </div>
            <div>
              <Label>Pricing tier</Label>
              <Select value={form.pricing_tier} onValueChange={(v) => setForm({ ...form, pricing_tier: v })}>
                <SelectTrigger data-testid="add-dealer-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{t} ({DISCOUNT[t]}% off)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="dealer-limit">Credit limit (₹)</Label>
              <Input id="dealer-limit" type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder="0" data-testid="add-dealer-limit" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="add-dealer-cancel">Cancel</Button>
            <Button onClick={submitDealer} disabled={saving} data-testid="add-dealer-submit">{saving ? "Saving…" : "Add dealer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
